/**
 * Camera Assignment Panel Component
 *
 * Super admin panel for managing Alta camera assignments to organizations.
 * Allows syncing from Alta API and assigning cameras to orgs.
 */

import {
  RefreshCw,
  Loader2,
  Server,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import {
  useAllAltaCameras,
  useSyncStatus,
  useAltaOrganizations,
  useSyncAltaCameras,
  useAssignCamera,
  useUnassignCamera,
  type AltaCamera,
} from '../../hooks/useCameras';

export function CameraAssignmentPanel() {
  // Data queries
  const { data: cameras, isLoading: camerasLoading, error: camerasError } = useAllAltaCameras();
  const { data: syncStatus } = useSyncStatus();
  const { data: organizations } = useAltaOrganizations();

  // Mutations
  const syncMutation = useSyncAltaCameras();
  const assignMutation = useAssignCamera();
  const unassignMutation = useUnassignCamera();

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const handleAssign = async (camera: AltaCamera, orgId: string) => {
    if (!orgId) {
      // Unassign
      await unassignMutation.mutateAsync(camera.id);
    } else {
      // Assign to org
      await assignMutation.mutateAsync({ altaCameraId: camera.id, orgId });
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-guild-mint/20 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-green/10">
              <Server size={20} className="text-guild-green" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Camera Registry</h3>
              <p className="text-xs text-gray-500">
                Assign cameras to organizations
              </p>
            </div>
          </div>

          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="
              inline-flex items-center gap-2 px-4 py-2.5
              text-sm font-semibold text-white bg-guild-green
              rounded-xl shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              disabled:opacity-50 disabled:cursor-not-allowed
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            {syncMutation.isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                Sync Cameras
              </>
            )}
          </button>
        </div>

        {/* Sync Stats */}
        {syncStatus && (
          <div className="mt-4 flex items-center gap-6 text-xs text-gray-600">
            <div className="flex items-center gap-1.5">
              <Server size={12} />
              <span><strong>{syncStatus.totalCameras}</strong> cameras</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Building2 size={12} />
              <span><strong>{syncStatus.assignedCameras}</strong> assigned</span>
            </div>
            <div className="flex items-center gap-1.5">
              <AlertCircle size={12} />
              <span><strong>{syncStatus.unassignedCameras}</strong> unassigned</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock size={12} />
              <span>Last sync: {formatDate(syncStatus.lastSyncedAt)}</span>
            </div>
          </div>
        )}

        {/* Sync Result */}
        {syncMutation.data && (
          <div className="mt-3 flex items-center gap-3 text-xs">
            <span className="px-2 py-1 rounded-full bg-green-100 text-green-700">
              +{syncMutation.data.added} added
            </span>
            <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700">
              {syncMutation.data.updated} updated
            </span>
            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700">
              {syncMutation.data.unchanged} unchanged
            </span>
            {syncMutation.data.errors.length > 0 && (
              <span className="px-2 py-1 rounded-full bg-red-100 text-red-700">
                {syncMutation.data.errors.length} errors
              </span>
            )}
          </div>
        )}
      </div>

      {/* Camera List */}
      <div className="overflow-x-auto">
        {camerasLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 size={24} className="animate-spin mr-2" />
            Loading cameras...
          </div>
        ) : camerasError ? (
          <div className="flex flex-col items-center justify-center py-12 text-red-600">
            <XCircle size={32} className="mb-2" />
            <p className="font-medium">Failed to load cameras</p>
            <p className="text-sm text-gray-500 mt-1">
              Make sure you have super admin access
            </p>
          </div>
        ) : cameras && cameras.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Camera
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Assigned To
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cameras.map((camera) => (
                <tr
                  key={camera.id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {camera.altaDeviceName}
                      </p>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">
                        {camera.altaDeviceId.substring(0, 8)}...
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-700">
                      {camera.altaLocation ?? '-'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`
                        inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                        ${camera.status === 'online'
                          ? 'bg-green-100 text-green-700'
                          : camera.status === 'offline'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600'}
                      `}
                    >
                      {camera.status === 'online' ? (
                        <CheckCircle2 size={12} />
                      ) : camera.status === 'offline' ? (
                        <XCircle size={12} />
                      ) : null}
                      {camera.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={camera.assignedOrgId ?? ''}
                      onChange={(e) => handleAssign(camera, e.target.value)}
                      disabled={assignMutation.isPending || unassignMutation.isPending}
                      className="
                        w-full max-w-xs rounded-lg border border-gray-200 bg-white
                        px-3 py-2 text-sm
                        focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                        disabled:bg-gray-50 disabled:text-gray-500
                        transition-all duration-200
                      "
                    >
                      <option value="">Unassigned</option>
                      {organizations?.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                    {camera.assignedOrg && (
                      <p className="text-xs text-gray-500 mt-1">
                        {camera.assignedByUser && (
                          <>
                            by {camera.assignedByUser.fullName}
                            {' '}
                          </>
                        )}
                        {camera.assignedAt && (
                          <span className="text-gray-400">
                            {new Date(camera.assignedAt).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Server size={32} className="mb-2 opacity-50" />
            <p className="font-medium">No cameras in registry</p>
            <p className="text-sm mt-1">
              Click "Sync Cameras" to fetch available cameras
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
